// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setModalOpen, closeModalWithResult } from "../../store/modalSlice";
import { getUrl } from "@aws-amplify/storage";
import ModalHeader from "../Modal/ModalHeader";
import { editAsset, fetchAssetsByGroupId } from "../../store/assetsSlice";
import ImageCropper from "./ImageCropper";
import ProfilePictureRounded from "../ProfilePictureRounded";
import { ReactComponent as ImageIcon } from "../../assets/icons/home-smile.svg";
import { imageCache } from "../../utils/imageCache";
import { UploadToS3 } from "./imageUploadModals/UploadToS3";
import { editAssetGroupFunc } from "../../store/assetGroupSlice";
import { updateUserRequest, updateOrganizationPicture } from "../../store/userSlice";

//This file contains the main ImageUploadFlow component and the ImageUploadTrigger component

// ImageUploadTrigger component for initiating the image upload flow
interface ImageUploadTriggerProps {
  label?: string;
  previewUrl?: string | null;
  onClick: () => void;
  buttonText?: string;
  placeholderText?: string;
  previewNode?: React.ReactNode;
}

// Component for triggering the image upload flow with preview
export const ImageUploadTrigger: React.FC<ImageUploadTriggerProps> = ({
  label = "Image (Optional)",
  previewUrl,
  onClick,
  buttonText,
  placeholderText = "No image",
  previewNode,
}) => {
  const resolvedButtonText = buttonText || (previewUrl ? "Change Image" : "Choose Image");

  return (
    <div className="mb-5">
      <label className="eai-meta-label mb-2 block">
        {label}
      </label>
      <div className="flex items-center space-x-4">
        <div className="flex-shrink-0">
          {previewNode ? (
            previewNode
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt={label}
              className="h-16 w-16 rounded-eai-card border border-eai-border object-cover"
            />
          ) : (
            <div className="eai-description-sm flex h-16 w-16 items-center justify-center rounded-eai-card border border-eai-border bg-eai-surface-sunken text-eai-body-xs">
              {placeholderText}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClick}
          className="eai-btn-cta"
        >
          {resolvedButtonText}
        </button>
      </div>
    </div>
  );
};

// Main ImageUploadFlow component
interface ImageUploadFlowProps {
  asset: any;
  onClose?: (result?: any) => void;
  uploadTarget?: "asset" | "assetGroup" | "profile" | "organization" | "generic";
  uploadPathBuilder?: (args: { file: File; assetId: string; identityId: string; organizationId?: string }) => string;
  onUploadSuccess?: (args: { imageUrl: string; file: File }) => Promise<void> | void;
  /** Overrides the "Update <name> Image" header — for targets whose name already reads as a noun. */
  title?: string;
}

const ImageUploadFlow: React.FC<ImageUploadFlowProps> = ({
  asset,
  onClose,
  uploadTarget,
  uploadPathBuilder,
  onUploadSuccess,
  title,
}) => {
  const [flowStep, setFlowStep] = useState<"crop" | "preview">("crop");
  // Removed flowSelectedFile because ImageCropper handles selection
  const [flowCroppedFile, setFlowCroppedFile] = useState<File | null>(null);
  const [flowPreviewUrl, setFlowPreviewUrl] = useState<string | null>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    const preloadExistingImage = async () => {
      if (asset?.imageUrl) {
        try {
          const { url } = await getUrl({
            path: asset.imageUrl,
            options: {
              validateObjectExistence: true,
              expiresIn: 3600,
            },
          });
          setFlowPreviewUrl(url.toString());
        } catch (error) {
          console.error("Error loading existing asset image:", error);
        }
      }
    };

    preloadExistingImage();
  }, [asset]);

  const handleFlowCropComplete = (croppedImageFile: File) => {
    setFlowCroppedFile(croppedImageFile);

    const reader = new FileReader();
    reader.onloadend = () => {
      setFlowPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(croppedImageFile);

    setFlowStep("preview");
  };

  const handleFlowCropCancel = () => {
    handleFlowClose();
  };

  const invalidateAndPrefetch = async (previousUrl?: string, nextUrl?: string) => {
    if (previousUrl) {
      imageCache.invalidate(previousUrl);
    }
    if (nextUrl) {
      await imageCache.getSignedUrl(nextUrl);
    }
  };

  const handleFlowSaveImage = async () => {
    if (!uploadTarget && !uploadPathBuilder) {
      throw new Error("uploadTarget is required unless a custom uploadPathBuilder is provided");
    }

    if (!flowCroppedFile) return;

    setUploadInProgress(true);
    setUploadError(null);

    try {
      console.log("Starting image upload process...");
      console.log("Asset data:", asset);
      console.log("Cropped file:", flowCroppedFile);

      // 1) Upload to S3
      const imageUrl = await UploadToS3.uploadImage({
        file: flowCroppedFile,
        assetId: asset.id,
        organizationId: asset.organizationId,
        target: uploadTarget,
        uploadPathBuilder,
        onProgress: setUploadPercent,
      });

      console.log("S3 upload successful, imageUrl:", imageUrl);

      // 2) Optional custom handler
      if (onUploadSuccess) {
        await onUploadSuccess({ imageUrl, file: flowCroppedFile });
        return; // ✅ finally still runs and resets progress/loading
      }

      // 3) Handle different upload targets

      // Asset group update path
      if (uploadTarget === "assetGroup") {
        try {
          const updateResult = await dispatch(
            editAssetGroupFunc({
              id: asset.id,
              profilePictureUrl: imageUrl,
              ...(asset?.organizationId && { organizationId: asset.organizationId }),
              ...(asset?.name && { name: asset.name }),
            })
          ).unwrap();

          console.log("Asset group update result:", updateResult);

          await invalidateAndPrefetch(asset?.profilePictureUrl || asset?.imageUrl, imageUrl);

          const result = {
            success: true,
            action: "updateImage",
            uploadTarget,
            assetGroupId: asset.id,
            imageUrl,
          };

          if (onClose) onClose(result);
          else dispatch(closeModalWithResult(result));
          return;
        } catch (err) {
          console.error("Error updating asset group image:", err);
          throw err; // bubble to outer catch if you want global handling too
        }
      }

      // Profile picture update path
      if (uploadTarget === "profile") {
        try {
          const updateResult = await dispatch(
            updateUserRequest({
              id: asset?.id,
              profilePictureUrl: imageUrl,
            })
          ).unwrap();

          console.log("Profile update result:", updateResult);

          await invalidateAndPrefetch(asset?.imageUrl, imageUrl);

          const result = {
            success: true,
            action: "updateImage",
            uploadTarget,
            profilePictureUrl: imageUrl,
          };

          if (onClose) onClose(result);
          else dispatch(closeModalWithResult(result));
          return;
        } catch (err) {
          console.error("Error updating profile picture:", err);
          throw err;
        }
      }

      // Organization picture update path
      if (uploadTarget === "organization") {
        try {
          await dispatch(
            updateOrganizationPicture({ id: asset.organizationId, profilePictureUrl: imageUrl })
          ).unwrap();

          await invalidateAndPrefetch(asset?.imageUrl, imageUrl);

          const result = {
            success: true,
            action: "updateImage",
            uploadTarget,
            organizationId: asset.organizationId,
            imageUrl,
          };

          if (onClose) onClose(result);
          else dispatch(closeModalWithResult(result));
          return;
        } catch (err) {
          console.error("Error updating organization picture:", err);
          throw err;
        }
      }

      // Asset update path
      if (uploadTarget === "asset") {
        try {
          const updatedAsset = {
            id: asset.id,
            imageUrl,
            name: asset.name,
            assetGroupId: asset.assetGroupId,
            lat: asset.lat,
            lng: asset.lng,
            elevation: asset.elevation,
            description: asset.description || "",
            ...(asset.organizationId && { organizationId: asset.organizationId }),
          };

          console.log("Updating asset with:", updatedAsset);

          const updateResult = await dispatch(editAsset(updatedAsset)).unwrap();
          console.log("Database update result:", updateResult);

          await invalidateAndPrefetch(asset?.imageUrl, imageUrl);

          if (asset.assetGroupId) {
            console.log("Refreshing assets for group:", asset.assetGroupId);
            await dispatch(fetchAssetsByGroupId(asset.assetGroupId)).unwrap();
          }

          const result = {
            success: true,
            action: "updateImage",
            uploadTarget,
            assetId: asset.id,
            imageUrl,
          };

          if (onClose) onClose(result);
          else dispatch(closeModalWithResult(result));
          return;
        } catch (err) {
          console.error("Error updating asset image:", err);
          throw err;
        }
      }

      // Generic success response when no built-in post-upload handling applies
      const result = {
        success: true,
        action: "uploadImage",
        imageUrl,
      };

      if (onClose) onClose(result);
      else dispatch(closeModalWithResult(result));
      return;
    } catch (error) {
      console.error("Error saving asset image:", error);
      setUploadError("We couldn't save the image. Please try again.");
    } finally {
      setUploadInProgress(false);
      setUploadPercent(0);
    }
  };

  const handleFlowRemoveImage = async () => {
    try {
      const baseResult: any = {
        success: true,
        action: "removeImage",
        uploadTarget,
        imageUrl: null,
      };

      // Include the right identifier so other modals don't mis-handle this event
      const result =
        uploadTarget === "assetGroup"
          ? { ...baseResult, assetGroupId: asset.id }
          : uploadTarget === "profile"
          ? { ...baseResult, userId: asset?.id, profilePictureUrl: null }
          : uploadTarget === "organization"
          ? { ...baseResult, organizationId: asset?.organizationId }
          : { ...baseResult, assetId: asset.id };

      if (onClose) onClose(result);
      else dispatch(closeModalWithResult(result));
    } catch (error) {
      console.error("Error removing asset image:", error);
    }
  };

  const resetFlow = () => {
    setFlowStep("crop");
    setFlowCroppedFile(null);
    setFlowPreviewUrl(null);
    setUploadInProgress(false);
    setUploadPercent(0);
    setUploadError(null);
  };

  const handleFlowClose = () => {
    if (onUploadSuccess && !onClose) {
      resetFlow();
      return;
    }

    if (onClose) onClose();
    else dispatch(setModalOpen(false));
  };

  return (
    <div>
      <ModalHeader 
        title={title || `Update ${asset?.name || 'Asset'} Image`}
        icon={<ImageIcon className="h-6 w-6" />}
        onClose={handleFlowClose}
        // Every host already pads this component, so the header must not add a second gutter
        gutterClassName="px-0"
      />
      
      <div className="mt-6">
        {flowStep === 'crop' && (
          <ImageCropper
            onCropComplete={handleFlowCropComplete}
            onCancel={handleFlowCropCancel}
          />
        )}

        {flowStep === 'preview' && flowCroppedFile && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="flex justify-center">
                <img 
                  src={flowPreviewUrl || ''}
                  alt="Cropped preview"
                  className="w-32 h-32 object-cover rounded-eai-card border border-eai-border"
                  style={{ width: '128px', height: '128px' }}
                />
              </div>
            </div>

            {uploadInProgress && (
              <div className="w-full bg-eai-border rounded-full h-2">
                <div 
                  className="bg-eai-cta-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadPercent}%` }}
                ></div>
              </div>
            )}

            {uploadError && (
              <p role="alert" className="eai-description-sm text-eai-danger-text">
                {uploadError}
              </p>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setUploadError(null); setFlowStep('crop'); }}
                disabled={uploadInProgress}
                className="eai-btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleFlowSaveImage}
                disabled={uploadInProgress}
                className="eai-btn-cta"
              >
                {uploadInProgress ? `Uploading ${uploadPercent}%` : 'Save Image'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploadFlow;
